import { LOGO_IMAGE_HIVE } from '@@partner:config/image'
import { Input, InputGroup, Popover, SelectPicker, Whisper, Loader, AutoComplete, IconButton, Tooltip, Tag, TagGroup } from 'rsuite'
import OffIcon from '@rsuite/icons/Off'
import MenuIcon from '@rsuite/icons/Menu'

import { usePartner } from '@@partner:helpers/hooks/auth'
import { notifyError } from '@@partner:helpers/notification'
import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { formatOrderStatusLabel } from '@@partner:helpers/formatters/order'
import { generatePath, Link, useHistory, useLocation } from 'react-router-dom'
import { ROUTE_INDEX, ROUTE_ORDERS } from '@@partner:config/routes'
import { useFetchOrdersByStatus } from '@@partner:graphql/query/order/ordersByStatus.gql'
import { useUniversalSearch } from '@@partner:graphql/query/universalSearch.gql'
import { mobileMenuOpenVar } from '@@partner:graphql/initial-data'
import { 
  buildUniversalSearchFilters, 
  formatSearchResults,
  getTypeLabel,
  type SearchResultItem 
} from '@@partner:helpers/search/universalSearch'

interface BrandOption {
  id: string | number
  name: string
  slug: string
}

export default function AppHeaderBar () {
  const { partnerInfo, setCurrentBrand, currentBrand, logout } = usePartner()

  const { data: ordersByStatusData, loading: ordersLoading } = useFetchOrdersByStatus()
  
  const history = useHistory()
  const location = useLocation()
  const currentBrandSlug = currentBrand?.slug
  const [ searchValue, setSearchValue ] = useState('')
  const [ searchResults, setSearchResults ] = useState<SearchResultItem[]>([])
  const [ selectedType, setSelectedType ] = useState<SearchResultItem['type'] | null>(null)
  const [ searchUniversal, { loading: searchLoading, data: searchData } ] = useUniversalSearch()
  
  // Debounce search
  useEffect(() => {
    if (!searchValue || searchValue.length < 2) {
      setSearchResults([])
      setSelectedType(null)
      return
    }

    const timeoutId = setTimeout(() => {
      const filters = buildUniversalSearchFilters(searchValue, currentBrand?.id)
      
      searchUniversal({
        variables: {
          ...filters,
          pager: { page: 1, limit: 10 }
        }
      })
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [ searchValue, currentBrand?.id, searchUniversal ])

  // Update search results when data changes
  useEffect(() => {
    if (searchData) {
      const results = formatSearchResults(searchData, currentBrandSlug as string)
      setSearchResults(results)
    }
  }, [ searchData, currentBrandSlug ])
  


  const ordersSummary = useMemo(() => {
    const groups = ordersByStatusData?.ordersByStatus ?? []

    return groups.map((group) => ({
      status: group.status,
      label: formatOrderStatusLabel(group.status).label,
      count: group.count
    }))
  }, [ ordersByStatusData ])

  const ordersTotal = useMemo(() => {
    return ordersSummary.reduce((acc, item) => acc + item.count, 0)
  }, [ ordersSummary ])

  const brandOptions = useMemo<BrandOption[]>(() => {
    return partnerInfo.brands.map((brand) => ({
      id: brand.id,
      name: brand.name,
      slug: brand.slug
    }))
  }, [ partnerInfo.brands ])

  const accountName = useMemo(() => {
    const parts = [ partnerInfo?.first_name, partnerInfo?.last_name ].filter(Boolean)
    return parts.length ? parts.join(' ') : '—'
  }, [ partnerInfo?.first_name, partnerInfo?.last_name ])

  const accountRole = useMemo(() => {
    const settings = partnerInfo?.settings ?? {}

    if (Array.isArray(settings.roles) && settings.roles.length) {
      return settings.roles[0]?.name ?? settings.roles[0]?.label ?? '—'
    }

    return settings.roleName ?? settings.role ?? '—'
  }, [ partnerInfo?.settings ])

  const selectedBrand = partnerInfo.brands.find(brand => currentBrand?.id === brand.id)

  const brandLogo = useMemo(() => (
    selectedBrand?.logo?.url ?? LOGO_IMAGE_HIVE
  ), [ selectedBrand?.logo?.url ])

  const handleBrandChange = useCallback(
    (nextSlug: string | null) => {
      if (!nextSlug || nextSlug === currentBrandSlug) return

      const nextBrand = brandOptions.find((brand) => brand.slug === nextSlug)

      if (!nextBrand) return

      setCurrentBrand({
        id: nextBrand.id,
        slug: nextBrand.slug
      })

      const slugToReplace = currentBrandSlug
      let targetPath = location.pathname

      if (slugToReplace && targetPath.includes(`/${slugToReplace}`)) {
        const brandSlugPattern = new RegExp(`/${slugToReplace}(?=/|$)`)
        targetPath = targetPath.replace(brandSlugPattern, `/${nextBrand.slug}`)
      }
      else {
        targetPath = generatePath(ROUTE_ORDERS, { brandSlug: nextBrand.slug })
      }

      if (targetPath !== location.pathname) {
        history.push({
          pathname: targetPath,
          search: location.search,
          hash: location.hash,
          state: location.state
        })
      }
    },
    [ brandOptions, currentBrandSlug, history, location, setCurrentBrand ]
  )

  const ordersPopover = useMemo(() => (
    <Popover title="Заказы в работе">
      <div className="PageWithSidebar__OrdersPopover">
        {ordersLoading ? (
          <span className="PageWithSidebar__OrdersPopoverEmpty">Загрузка...</span>
        ) : ordersSummary.length ? (
          ordersSummary.map(({ status, label, count }) => (
            <div key={status} className="PageWithSidebar__OrdersPopoverRow">
              <span className="PageWithSidebar__OrdersPopoverStatus">{label}</span>
              <span className="PageWithSidebar__OrdersPopoverCount">{count}</span>
            </div>
          ))
        ) : (
          <span className="PageWithSidebar__OrdersPopoverEmpty">Нет активных заказов</span>
        )}
      </div>
    </Popover>
  ), [ ordersLoading, ordersSummary ])

  const ordersInProgressLabel = ordersLoading
    ? '...'
    : ordersTotal > 0
      ? ordersTotal.toString()
      : '0'

  const handleSearchSelect = useCallback((value: string, item: any) => {
    // Игнорируем клики по заголовкам и disabled элементам
    if (item?.disabled || item?.isGroupHeader || value.startsWith('_')) {
      return
    }
    if (item?.url) {
      history.push(item.url)
      setSearchValue('')
      setSearchResults([])
      setSelectedType(null)
    }
  }, [ history ])

  const handleTypeFilter = useCallback((type: SearchResultItem['type'] | null) => {
    setSelectedType((prev) => prev === type ? null : type)
  }, [])

  const handleLogoutClick = useCallback(() => {
    logout()
      .then(() => {
        history.push(generatePath(ROUTE_INDEX))
      })
      .catch((err: Error) => {
        notifyError('Ошибка выхода', err.message)
      })
  }, [ history, logout ])

  // Подсчёт результатов по группам
  const groupCounts = useMemo(() => {
    const counts: Record<SearchResultItem['type'], number> = {
      customer: 0,
      order: 0,
      courier: 0,
      assembler: 0,
      supervisor: 0,
      operator: 0
    }
    searchResults.forEach((result) => {
      counts[result.type]++
    })
    return counts
  }, [ searchResults ])

  // Фильтрованные результаты
  const filteredResults = useMemo(() => {
    if (!selectedType) return searchResults
    return searchResults.filter((result) => result.type === selectedType)
  }, [ searchResults, selectedType ])

  const searchDropdownData = useMemo(() => {
    if (searchLoading) {
      return [ { label: 'Поиск...', value: '_loading', disabled: true } ]
    }
    
    if (!searchValue || searchValue.length < 2) {
      return []
    }

    if (searchResults.length === 0 && searchValue.length >= 2) {
      return [ { label: 'Ничего не найдено', value: '_empty', disabled: true } ]
    }

    // Группируем результаты по типу
    const groupedResults: Record<string, SearchResultItem[]> = {}
    filteredResults.forEach((result) => {
      const groupLabel = getTypeLabel(result.type)
      if (!groupedResults[groupLabel]) {
        groupedResults[groupLabel] = []
      }
      groupedResults[groupLabel].push(result)
    })

    // Формируем плоский массив с заголовками групп
    const flatData: any[] = []
    Object.keys(groupedResults).forEach((groupLabel) => {
      // Добавляем заголовок группы
      flatData.push({
        label: groupLabel,
        value: `_header_${groupLabel}`,
        disabled: true,
        isGroupHeader: true
      })
      
      // Добавляем элементы группы
      groupedResults[groupLabel].forEach((result) => {
        flatData.push({
          label: result.title,
          value: `${result.type}_${result.id}`,
          subtitle: result.subtitle,
          url: result.url,
          type: result.type,
          isGroupHeader: false
        })
      })
    })

    return flatData
  }, [ searchLoading, searchValue, searchResults, filteredResults ])


  const handleMobileMenuToggle = useCallback(() => {
    mobileMenuOpenVar(!mobileMenuOpenVar())
  }, [])

  return (
    <div className="PageWithSidebar__Topbar">
      <div className="PageWithSidebar__TopbarInner">
        <div className="PageWithSidebar__TopbarSection PageWithSidebar__TopbarSection--left">
          <Link to={ROUTE_INDEX} className="PageWithSidebar__LogoLink">
            <img className="PageWithSidebar__Logo" src={brandLogo} alt="Логотип" />
          </Link>
          <Whisper
            trigger={[ 'hover', 'click' ]}
            placement="autoVertical"
            speaker={ordersPopover}
            enterable
            preventOverflow
          >
            <button
              type="button"
              className="PageWithSidebar__Orders"
              aria-label="Заказы в работе"
              aria-busy={ordersLoading}
            >
              <span className="PageWithSidebar__OrdersLabel">Заказов в работе</span>
              <span className="PageWithSidebar__OrdersValue">{ordersInProgressLabel}</span>
            </button>
          </Whisper>
        </div>

        <div className="PageWithSidebar__TopbarSection PageWithSidebar__TopbarSection--center">
          <AutoComplete
            data={searchDropdownData}
            value={searchValue}
            onChange={(value) => {
              // Игнорируем значения заголовков
              if (value.startsWith('_header_') || value === '_loading' || value === '_empty') {
                return
              }
              setSearchValue(value)
            }}
            onSelect={handleSearchSelect}
            placeholder="Поиск: заказ, сотрудник, покупатель"
            filterBy={() => true}
            renderMenu={(menu) => {
              // Показываем чипсы только если есть результаты
              const hasResults = searchResults.length > 0 && searchValue.length >= 2
              const totalCount = Object.values(groupCounts).reduce((a, b) => a + b, 0)
              
              if (!hasResults) return menu
              
              const allTypeChips: { type: SearchResultItem['type']; label: string }[] = [
                { type: 'customer', label: 'Покупатели' },
                { type: 'order', label: 'Заказы' },
                { type: 'courier', label: 'Курьеры' },
                { type: 'assembler', label: 'Сборщики' },
                { type: 'supervisor', label: 'Супервайзеры' },
                { type: 'operator', label: 'Операторы' }
              ]
              const typeChips = allTypeChips.filter(({ type }) => groupCounts[type] > 0)
              
              return (
                <>
                  <div 
                    style={{ 
                      padding: '12px 16px 8px', 
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px'
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <Tag
                      size="sm"
                      color={selectedType === null ? 'blue' : undefined}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleTypeFilter(null)
                      }}
                    >
                      Все ({totalCount})
                    </Tag>
                    {typeChips.map(({ type, label }) => (
                      <Tag
                        key={type}
                        size="sm"
                        color={selectedType === type ? 'blue' : undefined}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTypeFilter(type)
                        }}
                      >
                        {label} ({groupCounts[type]})
                      </Tag>
                    ))}
                  </div>
                  {menu}
                </>
              )
            }}
            renderMenuItem={(label, item) => {
              if (item.isGroupHeader) {
                return (
                  <div style={{
                    padding: '10px 16px 6px',
                    fontWeight: 700,
                    fontSize: '0.7em',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#666',
                    backgroundColor: 'transparent',
                    marginTop: '8px',
                    marginBottom: '4px',
                    cursor: 'default'
                  }}>
                    {label}
                  </div>
                )
              }
              
              return (
                <div style={{ 
                  padding: '10px 16px',
                  cursor: 'pointer'
                }}>
                  <div style={{ fontWeight: 500, color: '#333' }}>{label}</div>
                  {item.subtitle && (
                    <div style={{ 
                      fontSize: '0.85em', 
                      color: '#666',
                      marginTop: '4px'
                    }}>
                      {item.subtitle}
                    </div>
                  )}
                </div>
              )
            }}
            style={{
              zIndex: 9999
            }}
          />
        </div>

        <div className="PageWithSidebar__TopbarSection PageWithSidebar__TopbarSection--right">
          <div className="PageWithSidebar__BrandPicker">
            <SelectPicker
              placement="bottomEnd"
              cleanable={false}
              data={brandOptions}
              value={currentBrandSlug}
              onChange={handleBrandChange}
              labelKey="name"
              valueKey="slug"
              placeholder="Выбор бренда"
              searchable
              style={{ minWidth: 200 }}
              menuStyle={{ marginTop: 8 }}
            />
          </div>
          <div className="PageWithSidebar__Account">
            <span className="PageWithSidebar__AccountName">{accountName}</span>
            <span className="PageWithSidebar__AccountRole">{accountRole}</span>
          </div>
          <div className="PageWithSidebar__LogoutBtn">
            <Whisper
              trigger="hover"
              placement="bottomEnd"
              speaker={<Tooltip>Выход</Tooltip>}
            >
              <IconButton
                icon={<OffIcon style={{ color: '#f44336' }} />}
                appearance="subtle"
                onClick={handleLogoutClick}
                style={{ marginLeft: 12 }}
              />
            </Whisper>
          </div>
          <IconButton
            icon={<MenuIcon />}
            appearance="subtle"
            onClick={handleMobileMenuToggle}
            className="PageWithSidebar__BurgerBtn"
          />
        </div>
      </div>
    </div>
  )
}